// Assinatura ampla o suficiente pra bater com o `t` real de useTexts()
// (que aceita count/params em formatos variados) sem acoplar a este util.
export type TranslateFn = (key: string, ...args: any[]) => string;

// Handoff Tela 5: "Número da agência" (placeholder "0000") · "Número da
// conta" (placeholder "00000-0") — dígitos, com dígito verificador opcional
// de 1 caractere após um hífen. O DV pode ser "X": convenção do Módulo 11
// pra representar resto 10 quando o cálculo do banco gera esse valor —
// usada com frequência pelo Banco do Brasil, tanto no dígito da agência
// quanto no da conta. Case-insensitive: contas editadas foram cadastradas
// por outro fluxo (components/auth/verification/BankAccount.vue), que não
// força maiúsculo no dígito — sem o `i`, uma conta antiga com "x" minúsculo
// reprova aqui e trava o botão Salvar na edição, sem erro visível (o campo
// só valida como "tocado" no blur). Vazio não é validado aqui (isFormValid
// já cobre "obrigatório" via botão desabilitado).
const AGENCY_ACCOUNT_FORMAT = /^\d+(-[\dX])?$/i;

export function isValidAgencyOrAccountNumber(value: string): boolean {
  return !value || AGENCY_ACCOUNT_FORMAT.test(value);
}

// Mesma regra de components/user/account/Pix.vue: chave aleatória PIX =
// 32 caracteres alfanuméricos.
const RANDOM_PIX_KEY_FORMAT = /^[a-zA-Z0-9]{32}$/;

export function isValidRandomPixKey(value: string): boolean {
  return !value || RANDOM_PIX_KEY_FORMAT.test(value.replace(/\W/g, ''));
}

// PIX só existe no Brasil: DDI(2) + DDD(2) + número(8 ou 9) = 12-13 dígitos.
// Aceita com máscara (ex.: "+55 (19) 99711-9007") — só dígitos contam,
// mesmo padrão de isValidRandomPixKey.
const PHONE_PIX_KEY_FORMAT = /^\d{12,13}$/;

export function isValidPhonePixKey(value: string): boolean {
  return !value || PHONE_PIX_KEY_FORMAT.test(value.replace(/\D/g, ''));
}

/**
 * Mapeia o status HTTP de um erro de `/bff/users/bank-account` pra uma
 * mensagem segura de mostrar ao jogador.
 *
 * Contrato BFF §4.6 e Guia PIX §6 documentam cada status; o ponto central é
 * que a `message` bruta do backend NUNCA deve ir pro toaster nos casos de
 * bloqueio de segurança (400/403) — ela vem com um sufixo interno de
 * diagnóstico (ex.: "Não foi possível processar esta operação. #PW003"),
 * que o próprio Guia PIX §6 instrui a nunca exibir pro jogador.
 *
 * `fallback` cobre 500 e qualquer status não mapeado — mantém o texto
 * específico de cada tela (ex.: "Não foi possível salvar a conta bancária.").
 */
export function getBankAccountErrorMessage(
  err: any,
  t: TranslateFn,
  fallback: string,
): string {
  switch (err?.status) {
    case 422: {
      // "Mostrar o erro do campo (body traz errors)" — Guia PIX §6.
      const fieldErrors = err?.data?.errors;
      const firstFieldError = fieldErrors && Object.values(fieldErrors)[0];

      return Array.isArray(firstFieldError)
        ? String(firstFieldError[0])
        : t('bank_account.errors.validation');
    }

    case 409:
      // 409 cobre motivos diferentes (conta duplicada, conta vinculada a
      // saques e não pode ser removida, etc.) — cada um com sua própria
      // `message` do backend, já pronta pro jogador (sem sufixo de
      // diagnóstico como em 400/403). Só cai no texto genérico se a API não
      // mandar mensagem nenhuma.
      return err?.data?.message || t('bank_account.errors.duplicate');

    case 404:
      return t('bank_account.errors.not_found');

    case 400:
      return t('bank_account.errors.security_block');

    case 403:
      return t('bank_account.errors.self_exclusion');

    default:
      return fallback;
  }
}
